import { CommonModule } from '@angular/common';
import { Component, Input, OnChanges, OnDestroy, SimpleChanges } from '@angular/core';
import { Subject, finalize, takeUntil } from 'rxjs';
import {
  LONGEVITY_AREA_LABELS,
  LONGEVITY_AREA_ORDER,
  LONGEVITY_GRADE_LABELS,
  MANAGER_CASE_STATUS_LABELS,
  LongevityAreaId,
  LongevityGrade,
  LongevityTestRow,
  ManagerCaseStatus,
} from '../../../alerts/longevity-alerts/longevity-alerts.model';
import { LongevityAssessmentModalComponent } from './longevity-assessment-modal.component';
import { LongevityRequestAssessmentsService } from './longevity-request-assessments.service';

/**
 * Elenco dei test Longevity compilati per una richiesta, mostrato nel tab "Registro" del dettaglio
 * richiesta al posto degli eventi (la Longevity non ne ha). La lista è leggera (data, punteggio, gradi,
 * stato del caso): i test si aprono uno alla volta con "Vedi risposte", che carica il dettaglio e apre una
 * modale (`LongevityAssessmentModalComponent`) con le domande, le risposte e gli editor di commento e piano.
 */
@Component({
  selector: 'app-longevity-request-assessments',
  standalone: true,
  imports: [CommonModule, LongevityAssessmentModalComponent],
  templateUrl: './longevity-request-assessments-list.component.html',
  styleUrl: './longevity-request-assessments-list.component.scss',
})
export class LongevityRequestAssessmentsListComponent implements OnChanges, OnDestroy {
  @Input({ required: true }) requestId!: number;

  isLoading = false;
  loadFailed = false;
  assessments: LongevityTestRow[] = [];

  /** Test aperto nella modale, null quando è chiusa. */
  selectedAssessment: LongevityTestRow | null = null;

  private unsubscribe = new Subject<void>();

  constructor(private service: LongevityRequestAssessmentsService) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['requestId']) {
      this.load();
    }
  }

  private load(): void {
    this.closeModal();
    this.isLoading = true;
    this.loadFailed = false;
    this.service
      .getAssessments(this.requestId)
      .pipe(
        takeUntil(this.unsubscribe),
        finalize(() => (this.isLoading = false))
      )
      .subscribe({
        next: (assessments) => {
          this.assessments = [...assessments].sort((a, b) => b.savedAt.getTime() - a.savedAt.getTime());
        },
        error: (err) => {
          console.error(`Longevity: elenco test della richiesta ${this.requestId} non disponibile`, err);
          this.loadFailed = true;
        },
      });
  }

  retry(): void {
    this.load();
  }

  areaLabel(area: LongevityAreaId): string {
    return LONGEVITY_AREA_LABELS[area];
  }

  gradeLabel(grade: LongevityGrade): string {
    return LONGEVITY_GRADE_LABELS[grade];
  }

  gradeClass(grade: LongevityGrade): string {
    return `grade-${grade === '-' ? 'none' : grade}`;
  }

  /**
   * I gradi di una riga della lista: `code` ha le quattro lettere nell'ordine Corpo, Mente, Longevità
   * finanziaria, Spazio collettivo. Una lettera sconosciuta vale "non calcolabile".
   */
  rowGrades(row: LongevityTestRow): { area: LongevityAreaId; grade: LongevityGrade }[] {
    return LONGEVITY_AREA_ORDER.map((area, i) => {
      const letter = row.code.charAt(i).toUpperCase();
      return { area, grade: letter === 'A' || letter === 'M' || letter === 'B' ? letter : '-' };
    });
  }

  caseStatusLabel(status: ManagerCaseStatus | null): string {
    return status ? MANAGER_CASE_STATUS_LABELS[status] : '-';
  }

  formatScore(score: number | null): string {
    return score === null ? '-' : new Intl.NumberFormat('it-IT', { maximumFractionDigits: 1 }).format(score);
  }

  openDetail(assessment: LongevityTestRow): void {
    this.selectedAssessment = assessment;
  }

  closeModal(): void {
    this.selectedAssessment = null;
  }

  ngOnDestroy(): void {
    this.unsubscribe.next();
    this.unsubscribe.complete();
  }
}
